import { Box, Paper, Typography, Link } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Helmet } from 'react-helmet';
import Fonts from './Fonts';
import { themeColors } from '../styles/theme';
import TopNav from './TopNav';
import AdSense from './AdSense';
import CoverageStats from './CoverageStats';
import ScrollIndicator from './ScrollIndicator';
import { Link as RouterLink } from 'react-router-dom';
import { Footer } from './Footer';

interface AboutProps {
    setTitle: Dispatch<SetStateAction<string>>;
    setShowBurgerMenuIcon: Dispatch<SetStateAction<boolean>>;
}

const About = ({
   setTitle,
   setShowBurgerMenuIcon
}: AboutProps) => {
    const [localTitle, setLocalTitle] = useState<string>("About");

    useEffect(() => {
        setTitle("About");
        setLocalTitle("About");
        setShowBurgerMenuIcon(true);
    }, // eslint-disable-next-line
        []);

    return (
        <>
        <Helmet>
            <title>About Suntap | Real-time Sun Tracking for UK Pub Gardens</title>
            <meta name="description" content="Learn how Suntap uses advanced 3D mapping and sun tracking technology to help you find the sunniest pub gardens across the UK. Join our community of pub enthusiasts." />
            <link rel="canonical" href="https://sun-tap.com/about" />
            <meta name="keywords" content="about suntap, 3D sun tracking, pub garden technology, UK pub finder, sunny beer gardens, real-time sun analysis" />
            <meta property="og:title" content="About Suntap | Real-time Sun Tracking for UK Pub Gardens" />
            <meta property="og:description" content="Learn how Suntap uses advanced 3D mapping and sun tracking technology to help you find the sunniest pub gardens across the UK." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://sun-tap.com/about" />
            <meta property="og:image" content="https://sun-tap.com/banner-1200-630.png" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="About Suntap | Real-time Sun Tracking for UK Pub Gardens" />
            <meta name="twitter:description" content="Learn how Suntap uses advanced 3D mapping and sun tracking technology to help you find the sunniest pub gardens across the UK." />
            <meta name="twitter:image" content="https://sun-tap.com/banner-1200-630.png" />
        </Helmet>
        <Fonts />
        <TopNav title={localTitle} />

        <Box 
            sx={{
                width: '100%',
                maxWidth: '900px',
                margin: '0 auto',
                padding: { xs: '0px 16px 16px', sm: '0px 24px 20px' },
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mt: 2,
            }}
        >
            <Typography
                variant="h1"
                sx={{
                    fontSize: '1.75rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                About Suntap
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    textAlign: 'justify',
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                Suntap is building the definitive guide to the UK's sunniest pub gardens. Our real-time solar engine fuses 3‑D building data with minute-by-minute sun-position calculations, showing whether a beer garden is in full light, when it will brighten up and—even down to seat-level—the best place to soak up the rays.
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    textAlign: 'justify',
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                While pub details are pulled from Google to keep venue information current, garden boundaries are mapped by our community. Every outline submitted sharpens the accuracy of our model and helps fellow pub-goers find their perfect sunny spot. Whether you're a local regular or just visiting, your input matters.
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    textAlign: 'justify',
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                Join the growing network of sun-seeking pint lovers and help create the most comprehensive, crowdsourced guide to Britain's bright-lit beer gardens. Full terms and privacy details are available on sun-tap.com. For questions or feedback, drop us a line at <a href="mailto:info@sun-tap.com">info@sun-tap.com</a>.
            </Typography>

            {/* Advertisement Section - Activate once we're approved for adsense */}
            <Box sx={{ width: '100%', my: 1, borderRadius: '40px' }}>
                <AdSense
                    client="ca-pub-3335995580070945"
                    slot="8169021635"
                    format="auto"
                    responsive="true"
                    width="100%"
                    height="250px"
                />
            </Box>
            
            {/* Wrapper for the CoverageStats to help with table sizing */}
            <Box
                sx={{
                    width: '100%',
                    overflow: 'hidden',
                    '& .MuiTableContainer-root': {
                        width: '100%',
                        maxWidth: '100%',
                    },
                    '& .MuiTable-root': {
                        tableLayout: 'fixed', // Fixed table layout helps with column width
                    },
                    '& .MuiTableCell-root': {
                        padding: { xs: '6px 4px', sm: '10px 8px' }, // Further reduce padding on xs screens
                        fontSize: { xs: '0.7rem', sm: '0.8rem' }, // Smaller font on mobile
                        wordBreak: 'break-word', // Handles long text better
                    },
                    '& .MuiTooltip-popper': {
                        zIndex: 1400 // Ensure tooltips are above other elements
                    }
                }}
            >
                <CoverageStats />
            </Box>

            {/* FAQ Section */}
                <Typography
                    variant="h2"
                    sx={{
                        fontSize: '1.5rem',
                        fontFamily: "ApfelGrotezkRegular",
                        color: themeColors.darkBackground,
                    }}
                >
                    Frequently Asked Questions
                </Typography>

                {/* FAQ Schema Markup */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {
                                "@type": "Question",
                                "name": "How does Suntap calculate sun exposure?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "We calculate the sun's position and shade the garden based on the geometry of the buildings and other structures. The result is a highly granular, moment-by-moment sunlight map."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Which cities are currently supported?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "We focus on London, Bristol and Brighton, but the underlying technology works anywhere that 3D mapping data exists. Additional UK cities are being added on a rolling basis."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "How accurate are the predictions?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Sun angles and building geometry are model-perfect, so clear-sky predictions are precise. Weather and temporary obstructions (such as parasols) aren't yet included, but future releases aim to integrate live weather feeds."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Does Suntap consider weather forecasts?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Not yet. Version 1 assumes clear conditions. Incorporating cloud-cover APIs is on our roadmap."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "How can I see which parts of a garden will be sunny?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "The 3‑D visualiser shades the garden: yellow for full sun, grey for shade. Drag the time-of-day slider to watch the light move and choose the ideal table."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Why is my favourite pub 'not supported'?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Some areas lack complete 3‑D data. We're working to expand coverage; you can help by submitting the garden outline."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Can I use Suntap abroad?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "The engine works anywhere with reliable 3‑D mapping. International locations are supported on an alpha basis, so please let us know if you have any feedback."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Does the app work offline?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Suntap is a Progressive Web App; basic pages cache for quick launch, but live calculations, 3-D geometry, place details and maps require a data connection."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "How do I share a sunny pub with friends?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Open the pub page, tap the share icon and choose QR code, WhatsApp, or your device's native share sheet."
                                }
                            }
                        ]
                    })}
                </script>

                {/* FAQ questions and answers */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                            }}
                        >
                            How does Suntap calculate sun exposure?
                        </Typography>
                        <Typography variant="body1">
                            We calculate the sun's position and shade the garden based on the geometry of the buildings and other structures. The result is a highly granular, moment-by-moment sunlight map.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                            }}
                        >
                            Which cities are currently supported?
                        </Typography>
                        <Typography variant="body1">
                            We focus on London, Bristol and Brighton, but the underlying technology works anywhere that 3D mapping data exists. Additional UK cities are being added on a rolling basis.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                            }}
                        >
                            How accurate are the predictions?
                        </Typography>
                        <Typography variant="body1">
                            Sun angles and building geometry are model-perfect, so clear-sky predictions are precise. Weather and temporary obstructions (such as parasols) aren't yet included, but future releases aim to integrate live weather feeds.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                            }}
                        >
                            Does Suntap consider weather forecasts?
                        </Typography>
                        <Typography variant="body1">
                            Not yet. Version 1 assumes clear conditions. Incorporating cloud-cover APIs is on our roadmap.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                            }}
                        >
                            How can I see which parts of a garden will be sunny?
                        </Typography>
                        <Typography variant="body1">
                            The 3‑D visualiser shades the garden: yellow for full sun, grey for shade. Drag the time-of-day slider to watch the light move and choose the ideal table.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                                mb: 1
                            }}
                        >
                            Why is my favourite pub 'not supported'?
                        </Typography>
                        <Typography variant="body1">
                            Some areas lack complete 3‑D data. We're working to expand coverage; you can help by submitting the garden outline.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                            }}
                        >
                            Can I use Suntap abroad?
                        </Typography>
                        <Typography variant="body1">
                            The engine works anywhere with reliable 3‑D mapping. International locations are supported on an alpha basis, so please let us know if you have any feedback.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                            }}
                        >
                            Does the app work offline?
                        </Typography>
                        <Typography variant="body1">
                            Suntap is a Progressive Web App; basic pages cache for quick launch, but live calculations, 3-D geometry, place details and maps require a data connection.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: '1.2rem',
                                fontWeight: 600,
                                color: themeColors.darkBackground,
                                mb: 1
                            }}
                        >
                            How do I share a sunny pub with friends?
                        </Typography>
                        <Typography variant="body1">
                            Open the pub page, tap the share icon and choose QR code, WhatsApp, or your device's native share sheet.
                        </Typography>
                    </Box>
                </Box>

                <Typography
                    variant="body1"
                    sx={{
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        lineHeight: 1.6,
                        color: themeColors.darkBackground,
                        textAlign: 'center',
                        fontFamily: "ApfelGrotezkRegular",
                        fontStyle: 'italic',
                        mt: 4,
                        pt: 3,
                        borderTop: '1px solid #e0e0e0'
                    }}
                >
                    Together we can make hidden courtyards and pop-up terraces just as easy to find as the big beer gardens—always at the exact moment they're drenched in sunshine.
                </Typography>
        </Box>

        {/* Add Footer */}
        <Box sx={{ px: '5%' }}>
            <Footer />
        </Box>

        <ScrollIndicator
            forceVisible={true}
            iconColor={themeColors.orange}
            backgroundColor="rgba(255, 255, 255, 0.8)"
        />
        </>
    );
};

export default About;